import React, { useId } from 'react';
import { Check, CircleAlert, Minus, RotateCw } from 'lucide-react';
import { cx, disc } from './classes';

/**
 * Shown instead of ChangeList on V1, when there's nothing to compare against yet. Pass other copy when
 * there are no moves to make (Nothing urgent).
 */
export const FirstVersionNote = ({
  children = 'This is the first version. After you make the moves below in your DAW, upload V2 here to see what improved.',
}) => (
  <div className="flex items-center gap-space-3 p-space-4 rounded-radius-md border border-dashed border-line">
    <span className={cx(disc, 'bg-raised text-ink-soft')}>
      <RotateCw size={16} strokeWidth={2.5} aria-hidden="true" />
    </span>
    <span className="font-text text-small text-ink-soft">{children}</span>
  </div>
);

/**
 * "What changed since V1": plain-language verdicts on the notable changes, each
 * { label, verdict, needsAttention, explanation }, then one sentence for everything else. Fine changes
 * are neutral; only changes that need attention are coral. Without a previous version (V1) it shows
 * the first-version note instead, with `firstVersionNote` as its copy when given.
 */
export const ChangeList = ({ previousVersion, changes = [], everythingElse, firstVersionNote }) => {
  const headingId = useId();
  if (!previousVersion) return firstVersionNote ? <FirstVersionNote>{firstVersionNote}</FirstVersionNote> : <FirstVersionNote />;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-space-3">
      <h3 id={headingId} className="m-0 font-display text-section text-ink">
        What changed since V{previousVersion}
      </h3>
      <ul className="m-0 p-0 list-none rounded-radius-md bg-base">
        {changes.map((change) => (
          <li key={change.label} className="flex items-start gap-space-3 p-space-4 border-b border-raised last:border-b-0">
            <span className={cx(disc, change.needsAttention ? 'bg-coral-tint text-coral' : 'bg-raised text-ink-soft')}>
              {change.needsAttention
                ? <CircleAlert size={16} strokeWidth={2.5} aria-hidden="true" />
                : <Check size={16} strokeWidth={2.5} aria-hidden="true" />}
            </span>
            <div className="flex flex-col gap-space-1 font-text">
              <span className="text-body-strong text-ink">
                {change.label}{' '}
                <span className={change.needsAttention ? 'text-coral' : 'font-normal text-ink-soft'}>· {change.verdict}</span>
                {change.needsAttention && <span className="sr-only"> (needs attention)</span>}
              </span>
              {change.explanation && <span className="text-small text-ink-soft">{change.explanation}</span>}
            </div>
          </li>
        ))}
        {everythingElse && (
          <li className="flex items-center gap-space-3 px-space-4 py-space-3">
            <span className={cx(disc, 'text-muted')}>
              <Minus size={16} strokeWidth={2.5} aria-hidden="true" />
            </span>
            <span className="font-text text-small text-muted">{everythingElse}</span>
          </li>
        )}
      </ul>
    </section>
  );
};
